/**
 * useFlowHistory - the editor's undo / redo as a hook around the pure stacks
 * in history.ts.
 *
 * The stacks live in a ref (so two edits in one tick both land) and a counter
 * re-renders the component when they change (so the toolbar's undo / redo
 * buttons enable and disable). The functions are stable across renders. Turning
 * history off (the map became read-only) forgets the stacks.
 */
import { useCallback, useEffect, useMemo, useReducer, useRef } from "react";
import { emptyHistory, historyReducer, redoTarget, undoTarget, type FlowHistory, type FlowHistoryAction } from "./history.js";
import type { FlowMapDoc } from "./model.js";

export interface FlowHistoryControls {
  canUndo: boolean;
  canRedo: boolean;
  /** An edit was committed: remember the document it replaced. */
  record: (before: FlowMapDoc) => void;
  /** The document to go back to (null: nothing to undo); `current` becomes redoable. */
  undo: (current: FlowMapDoc) => FlowMapDoc | null;
  /** The document to go forward to (null: nothing to redo); `current` becomes undoable. */
  redo: (current: FlowMapDoc) => FlowMapDoc | null;
  /** Forget everything (the document was replaced from outside). */
  clear: () => void;
}

export function useFlowHistory(enabled: boolean): FlowHistoryControls {
  const stacks = useRef<FlowHistory<FlowMapDoc>>(emptyHistory());
  const [version, bump] = useReducer((n: number) => n + 1, 0);
  const on = useRef(enabled);
  on.current = enabled;

  const dispatch = useCallback((action: FlowHistoryAction<FlowMapDoc>): void => {
    const next = historyReducer(stacks.current, action);
    if (next === stacks.current) return;
    stacks.current = next;
    bump();
  }, []);

  const record = useCallback(
    (before: FlowMapDoc): void => {
      if (on.current) dispatch({ type: "record", before });
    },
    [dispatch],
  );

  const undo = useCallback(
    (current: FlowMapDoc): FlowMapDoc | null => {
      const target = on.current ? undoTarget(stacks.current) : undefined;
      if (target === undefined) return null;
      dispatch({ type: "undo", current });
      return target;
    },
    [dispatch],
  );

  const redo = useCallback(
    (current: FlowMapDoc): FlowMapDoc | null => {
      const target = on.current ? redoTarget(stacks.current) : undefined;
      if (target === undefined) return null;
      dispatch({ type: "redo", current });
      return target;
    },
    [dispatch],
  );

  const clear = useCallback((): void => dispatch({ type: "clear" }), [dispatch]);

  useEffect(() => {
    if (!enabled) clear();
  }, [enabled, clear]);

  return useMemo(() => {
    const h = stacks.current;
    return {
      canUndo: enabled && h.past.length > 0,
      canRedo: enabled && h.future.length > 0,
      record,
      undo,
      redo,
      clear,
    };
    // `version` is the signal that the stacks in the ref changed.
  }, [enabled, version, record, undo, redo, clear]);
}
